import React, { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { Age, Patient } from '../store/types/patient';
import { getUnit, toTitleCase } from '../store/utils';
import { NewPatient as AddPatientNewPatient } from '../store/actions/app';
import { DFormEntity } from '../api/fetchDoctorGenieByOID';
import PatientInfoForm from '../components/common/PatientInfoForm';
import { Gender } from './NewPatient';
import { MISC_KEYS, getDoc, getItemFromMisc, getSelectedClinicId } from '../store/idbHandler';
import { checkIfValidPhoneNumber, getAddPDForms, getPatientPicUrl } from './utils';
import { dispatch } from '../bridge/events/dispatch';
import {
    ADD_NEW_PT_APPT,
    NEW_PT_FORM_DATA,
    NEW_PT_FORM_DATA_AS_SELECTED,
} from '../bridge/events/types';
import { DAYS, MONTHS, YEARS } from '../components/common/constants/add-patient';
import fetchPtMedHistory from '../api/fetchPtMedHistory';
import { MHC } from '../store/types/medicalHistory';
import { GenieSettings, MedicalHistoryConfig } from '../api/pushGenie';
import { ConfigTabs } from '../components/prescriptionPad/rightPanel/workspace/padV2/ConfigureElementsTableDialog';
import { track } from '../tracking';
import { getPrescription, isMHFilled } from '../store/selectors/prescriptionPad';
import { captureError } from './captureError';
import { IFormState } from '../components/common/types/add-patient';
import { notification } from 'antd';
import { CountryCode } from 'libphonenumber-js';
import { MyDoc } from '../api/fetchMyProfile';
import { DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES } from '../constants';
import { getMyIdWithBackwardCompatibility } from '../api/mactiveid';
import { GlobalState } from '../store/reducers';
import { EKA_EVENT, dispatch as ekaEventDispath } from '../bridge/events/dispatch';
import { his } from './history';
import { GO_TO_NEW_APPOINTMENT } from '../routes/paths';

export type Data = {
    name?: string;
    phCode: string;
    phCodeEnum?: CountryCode;
    phoneNumber?: string;
    dob?: {
        dd?: string;
        mm?: string;
        yyyy?: string;
    };
    age?: string;
    ageMonth?: string;
    ageDays?: string;
    gender?: Gender;

    isDobError: boolean;
    isAgeDOBError?: boolean;
    isNameError: boolean;
    isGenderError: boolean;
    isPhoneInputDisabled: boolean;
    isNameInputDisabled: boolean;
    isAgeInputDisabled: boolean;
    isGenderInputDisabled: boolean;
    isPhoneNumberError: boolean;
    isReferralPhoneNumberError: boolean;

    dfValues: { [k: string]: string | number };
    pic?: string | File;
    localId?: string;
    referDocId?: string;

    abhaId?: string;
    abhaOid?: string;
};

type State = Data & {
    pic?: string | File;
    dForm?: DFormEntity[];
    isPhCode?: boolean;
    ptMHHistory?: MHC;
    clearMHAfterInputChange?: boolean;
    hasNoMedicalHistory?: boolean;
    abhaId?: string;
    createSkipState?: string;
    authHealthId?: string | undefined;
    showSendOtpScreen: boolean;
    ptId?: string;
    autoSelectPt?: Patient;
    allowEkaEvent: boolean;
    fromNdhmDiscovery?: boolean;
    medicalHistoryConfigData: MedicalHistoryConfig;
    showMedicalHistoryInAddPt: boolean;
    abhaOid?: string;
};

type Props = {
    isLoading?: boolean;
    data: Data;
    ActionBar: React.FC<{
        onCancel: () => void;
        onSave: () => void;
        isLoading?: boolean;
        isDisabled?: boolean;
        isAbdmEnabled?: boolean;
        showAbhaCard?: boolean;
        setAddPtFormState?: (val: IFormState | undefined) => void;
        setShowAbhaCardHandler?: (val: boolean) => void;
    }>;
    actionBarProps?: { isLoading?: boolean };
    FillerComp: React.FC<{
        Form: ReactNode;
        ActionBar: ReactNode;
        onDyFormUpdate: () => void;
        isDialogVisible: boolean;
        setDialogVisibility: (visible: boolean) => void;
        closeMe: () => void;
        configureTab?: ConfigTabs;
        formState?: IFormState;
        onRefresh: () => void;
    }>;
    actions: {
        onCancel: () => void;
        onSave: (newPatient: AddPatientNewPatient, pic?: File, mh?: MHC, doc?: MyDoc) => void;
        isLoading?: boolean;
    };
    isDialogVisible: boolean;
    setDialogVisibility: (visible: boolean, tab?: ConfigTabs) => void;
    showAddFields?: boolean;
    showUseABDM?: boolean;
    pic?: string | File;
    patientEditFlow?: boolean;
    configureTab?: ConfigTabs;
    medicalHistory?: MHC | undefined;
    showMHConfigure?: boolean;
    isAbdmEnabled?: boolean;
    abhaNewFlow?: boolean;
    showAbhaCard?: boolean;
    setShowAbhaCardHandler: (val: boolean) => void;
    formState: IFormState | undefined;
    setFormState?: (val: IFormState | undefined) => void;
    isSendAbhaOtp?: boolean;
    rxDocId: string;
    patientId?: string;
};

export const decodeAge = (
    age?: Age,
): { dob?: { dd: string; mm: string; yyyy: string }; age?: string; ageMonth?: string } => {
    if (age?.dob) {
        const dobDate = new Date(age.dob);
        const ddDOB = dobDate.getDate();
        const mmDOB = dobDate.getMonth() + 1;
        const yyyyDOB = dobDate.getFullYear();
        return {
            dob: {
                dd: ddDOB.toString(),
                mm: mmDOB.toString(),
                yyyy: yyyyDOB.toString(),
            },
            age: undefined,
            ageMonth: undefined,
        };
    } else {
        return {
            dob: undefined,
            age: age?.value,
            ageMonth: age?.month,
        };
    }
};

class PatientInfoComp extends PureComponent<Props, State> {
    state: State = {
        ...this.props.data,
        ptMHHistory: this.props.medicalHistory ?? {},
        hasNoMedicalHistory: false,
        abhaId: this.props.data.abhaId,
        abhaOid: this.props.data.abhaOid,
        createSkipState: undefined,
        showSendOtpScreen: false,
        allowEkaEvent: false,
        medicalHistoryConfigData: DEFAULT_MEDICAL_HISTORY_CONFIG_VALUES,
        showMedicalHistoryInAddPt: true,
    };

    fillNewPtData = (data: Data) => {
        const phoneNumber = data?.phoneNumber?.replace('+91', '');

        const d = {
            name: data?.name || this.state?.name,
            phoneNumber: phoneNumber || this.state?.phoneNumber,
            phCode: data.phCode || this.state?.phCode || '+91',
            dob: data?.dob || this.state?.dob,
            age: data?.age || this.state?.age,
            ageMonth: data?.ageMonth || this.state?.ageMonth,
            ageDays: data?.ageDays || this.state?.ageDays,
            gender: data?.gender || this.state?.gender,
            dfValues: data?.dfValues || this.state?.dfValues,
            pic: data?.pic || this.state?.pic,
            isPhoneInputDisabled: true,
            isNameInputDisabled: true,
            isAgeInputDisabled: true,
            isGenderInputDisabled: true,
        };

        this.setState(d);
    };

    componentDidMount = () => {
        this.onDyFormUpdate();
        this.setState({ pic: this.props?.pic });
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.fillNewPtData = this.fillNewPtData;
        const { getMedicalHistoryConfigData, getShowMedicalHistoryInAddPt } = this;
        getMedicalHistoryConfigData();
        getShowMedicalHistoryInAddPt();
    };

    getMedicalHistoryConfigData = () => {
        const { rxDocId } = this.props;
        const myId = getMyIdWithBackwardCompatibility() || '';
        const currentUserId = rxDocId || myId || '';
        getItemFromMisc<MedicalHistoryConfig>(currentUserId, MISC_KEYS.medicalHistoryConfig)
            .then((medicalHistoryConfigData) => {
                this.setState({
                    medicalHistoryConfigData: medicalHistoryConfigData,
                });
            })
            .catch(console.error);
    };

    getShowMedicalHistoryInAddPt = () => {
        const { rxDocId } = this.props;
        const myId = getMyIdWithBackwardCompatibility() || '';
        const currentUserId = rxDocId || myId || '';
        getItemFromMisc<GenieSettings>(currentUserId, MISC_KEYS.settings)
            .then((settings) => {
                this.setState({
                    showMedicalHistoryInAddPt: settings.showMedicalHistoryInAddPt,
                });
            })
            .catch(console.error);
    };

    onNameChange = (input?: string): void => {
        if (input === undefined) {
            this.setState({ name: '' });
            return;
        }

        const { pic, clearMHAfterInputChange } = this.state;
        this.setState({
            name: input,
            isNameError: input.length === 0,
            pic: this.isEditMode() || this.isPicFileType() ? pic : undefined,
        });

        if (clearMHAfterInputChange) {
            this.setState({ ptMHHistory: {}, clearMHAfterInputChange: false });
        }
    };

    onDFChange = (df: DFormEntity) => (v: string): void => {
        let input: string | number = v;

        if (input === '') {
            this.setState({
                dfValues: {
                    ...this.state.dfValues,
                    [df.key]: '',
                },
            });

            return;
        }

        if (df.key === 'referredbynumber') {
            this.setState({
                isReferralPhoneNumberError: !checkIfValidPhoneNumber('+91', input, 'IN'),
            });
        }

        if (df.type === 'number') {
            input = parseFloat(input.replace(/[^0-9]/g, ''));

            if (isNaN(input)) {
                return;
            }
        }

        this.setState({
            dfValues: {
                ...this.state.dfValues,
                [df.key]: input,
            },
        });
    };

    isEditMode() {
        if (this.props?.patientEditFlow) {
            return true;
        }
        return false;
    }

    isPicFileType() {
        const { pic } = this.state;
        if (pic?.constructor?.name === 'File') {
            return true;
        }
        return false;
    }

    onPhoneNumberChange = (input?: string): void => {
        const { phCode, pic, clearMHAfterInputChange } = this.state;

        this.setState({
            phoneNumber: input,
            pic: this.isEditMode() || this.isPicFileType() ? pic : undefined,
        });

        if (clearMHAfterInputChange) {
            this.setState({ ptMHHistory: {}, clearMHAfterInputChange: false });
        }
    };

    onCountryCodeChange = (input: string, countryCodeEnum: CountryCode): void => {
        const { phCode, pic, clearMHAfterInputChange } = this.state;
        this.setState({
            phCode: input,
            phCodeEnum: countryCodeEnum,
            pic: this.isEditMode() || this.isPicFileType() ? pic : undefined,
        });

        if (this.state.isPhoneNumberError) {
            this.setState({
                isPhoneNumberError: !checkIfValidPhoneNumber(phCode, input, this.state.phCodeEnum),
            });
        }

        if (clearMHAfterInputChange) {
            this.setState({ ptMHHistory: {}, clearMHAfterInputChange: false });
        }
    };

    onDDDobChange = (e: React.ChangeEvent<HTMLSelectElement> | string): void => {
        const input = typeof e === 'string' ? e : e.target.value;
        this.setState({
            dob: this.state.dob ? { ...this.state.dob, dd: input } : { dd: input },
            age: '0',
            ageMonth: '0',
            ageDays: undefined,
        });
    };

    clearDob = (val?: string) => {
        this.setState({
            dob: {
                dd: '0',
                mm: '0',
                yyyy: '0',
            },
            age: undefined,
            ageDays: undefined,
            ageMonth: undefined,
        });
    };

    onMMDobChange = (e: React.ChangeEvent<HTMLSelectElement> | string): void => {
        const input = typeof e === 'string' ? e : e.target.value;

        this.setState({
            dob: { ...this.state.dob, mm: input },
            age: '0',
            ageMonth: '0',
            ageDays: undefined,
        });
    };

    onYYYYDobChange = (e: React.ChangeEvent<HTMLSelectElement> | string): void => {
        const input = typeof e === 'string' ? e : e.target.value;

        this.setState({
            dob: { ...this.state.dob, yyyy: input },
            age: '0',
            ageMonth: '0',
            ageDays: undefined,
        });
    };

    onGenderChange = (gender?: Gender): void => {
        this.setState({
            gender,
            isGenderError: false,
        });
    };

    setMedicalHistory = (mh: MHC | undefined) => {
        this.setState({ ptMHHistory: mh });
    };

    errorValidation = () => {
        const {
            name,
            phCode,
            phoneNumber,
            dob,
            age,
            gender,
            ageMonth,
            ageDays,
            isDobError,
        } = this.state;

        const isAgeDOBAbsent =
            !age &&
            !ageDays &&
            !ageMonth &&
            (!dob || (dob?.dd === '0' && dob?.mm === '0' && dob?.yyyy === '0'));

        if (
            !name ||
            (phoneNumber && !checkIfValidPhoneNumber(phCode, phoneNumber, this.state.phCodeEnum)) ||
            !gender ||
            (this.state?.dfValues?.['referredbynumber'] &&
                !checkIfValidPhoneNumber(
                    '+91',
                    this.state?.dfValues?.['referredbynumber']?.toString(),
                    'IN',
                )) ||
            isAgeDOBAbsent
        ) {
            if (
                phoneNumber &&
                !checkIfValidPhoneNumber(phCode, phoneNumber, this.state.phCodeEnum)
            ) {
                this.setState({
                    isPhoneNumberError: true,
                });
            }

            if (
                this.state?.dfValues?.['referredbynumber'] &&
                !checkIfValidPhoneNumber(
                    '+91',
                    this.state?.dfValues?.['referredbynumber']?.toString(),
                    'IN',
                )
            ) {
                this.setState({
                    isReferralPhoneNumberError: true,
                });
            }

            if (!name) {
                this.setState({
                    isNameError: true,
                });
            }

            if (!gender) {
                this.setState({
                    isGenderError: true,
                });
            }

            if (isAgeDOBAbsent) {
                this.setState({
                    isAgeDOBError: true,
                });
            }

            return {
                success: false,
                data: { isAgeDOBAbsent, isDobError, name, phoneNumber, gender },
            };
        }

        let dobStr = '';

        if (ageDays) {
            const MS_IN_DAY = 24 * 60 * 60 * 1000;
            const todayDay = new Date().getTime() / MS_IN_DAY;
            const desiredDay = todayDay - parseInt(ageDays);
            dobStr = moment(new Date(desiredDay * MS_IN_DAY)).format('YYYY-MM-DD');
        } else if (dob && (dob.dd !== '0' || dob.mm !== '0' || dob.yyyy != '0')) {
            const dobDate = moment(`${dob.yyyy}-${dob.mm}-${dob.dd}`, 'YYYY-MM-DD');
            if (
                !dobDate.isValid() ||
                dobDate.isAfter(new Date()) ||
                dob.yyyy === '0' ||
                !dob.dd ||
                !dob.mm ||
                !dob.yyyy
            ) {
                // moment js consider 0 as 2000
                this.setState({
                    isDobError: true,
                    isAgeDOBError: false,
                });

                return {
                    success: false,
                    data: { isAgeDOBAbsent, isDobError, name, phoneNumber, gender },
                };
            } else {
                dobStr = dobDate.format('YYYY-MM-DD');
                this.setState({
                    isDobError: false,
                    isAgeDOBError: false,
                });
            }
        }

        return { success: true, dobStr };
    };

    setAbhaOid = (oid?: string) => {
        this.setState({
            abhaOid: oid,
        });
    };

    onSave = (doc?: MyDoc): void => {
        const {
            ptMHHistory,
            hasNoMedicalHistory,
            pic,
            name,
            phCode,
            phoneNumber,
            gender,
            age,
            ageMonth,
            referDocId,
            showMedicalHistoryInAddPt,
            abhaOid,
        } = this.state;

        const { showAbhaCard } = this.props;

        const result = this.errorValidation();

        const newPatientMH = {
            patientMedicalConditions: ptMHHistory?.patientMedicalConditions,
            familyHistory: ptMHHistory?.familyHistory,
            lifestyleHabits: ptMHHistory?.lifestyleHabits,
            recentTravelHistory: ptMHHistory?.recentTravelHistory,
            currentMedications: ptMHHistory?.currentMedications,
            drugAllergy: ptMHHistory?.drugAllergy,
            foodOtherAllergy: ptMHHistory?.foodOtherAllergy,
            pastProcedures: ptMHHistory?.pastProcedures,
            otherMedicalHistory: ptMHHistory?.otherMedicalHistory,
        };

        if (result.success && name) {
            const newPatient: AddPatientNewPatient = {
                oid: abhaOid,
                name,
                phCode,
                phoneNumber,
                age: result.dobStr ? { dob: result.dobStr } : { value: age, month: ageMonth },
                gender: gender?.toString() || '',
                formData: this.state.dForm?.map((df) => ({
                    ...df,
                    value: this.state.dfValues[df.key],
                })),
                refer_doc_id: referDocId,
            };

            const finalMH =
                !isMHFilled(newPatientMH) && !hasNoMedicalHistory
                    ? undefined
                    : hasNoMedicalHistory
                    ? {}
                    : newPatientMH;

            this.props.actions.onSave(
                newPatient,
                this.isPicFileType() ? (pic as File) : undefined,
                showMedicalHistoryInAddPt ? finalMH : undefined,
                doc,
            );
        } else {
            notification.error({
                message: 'Please fill all the required fields',
                duration: 5,
            });

            if (showAbhaCard) {
                const error = `Please fill all the required fields: 
                        name: ${result.data?.name}, 
                        phoneNumber: ${result.data?.phoneNumber}, 
                        gender: ${result.data?.gender}, 
                        isAgeDOBAbsent: ${result.data?.isAgeDOBAbsent}, 
                        isDobError: ${result.data?.isDobError}`;

                // captureError(new Error(`ABHA: ${error}`));

                track('ADD_PATIENT_VALIDATION_FAILED', {
                    isNewAbhaFlow: String(showAbhaCard),
                    name: result.data?.name,
                    phoneNumber: result.data?.phoneNumber,
                    gender: result.data?.gender,
                    isAgeDOBAbsent: String(result.data?.isAgeDOBAbsent),
                    isDobError: String(result.data?.isDobError),
                });
            }
        }
    };

    onAddEkaRefer = (referDocId?: string, name?: string, number?: string) => {
        this.setState({
            referDocId: referDocId,
            dfValues: {
                ...this.state.dfValues,
                referredby: name || '',
                referredbynumber: number || '',
            },
            isReferralPhoneNumberError: number
                ? !checkIfValidPhoneNumber('+91', `${number}`, 'IN')
                : false,
        });
    };

    fetchMH = async (patient?: Patient) => {
        try {
            const doc = getDoc();
            const medicalHistory = await fetchPtMedHistory(doc?._id ?? '', patient?.id ?? '');

            return medicalHistory.data;
        } catch (error) {
            throw error;
        }
    };

    setAbhaFields = (opt: { skipState: string; abhaId: string | undefined }) => {
        this.setState({
            abhaId: opt.abhaId,
            createSkipState: opt.skipState,
        });

        this.props.setShowAbhaCardHandler(true);
    };

    onAutoComplete = (patient?: Patient): void => {
        if (
            !this.props.isSendAbhaOtp &&
            this.props.showUseABDM &&
            !this.props.showAbhaCard &&
            this.props.formState === 'add-pt'
        ) {
            dispatch({
                type: NEW_PT_FORM_DATA_AS_SELECTED,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                data: { ...patient, localId: undefined },
            });
        }

        if (!patient) {
            if (this.state.fromNdhmDiscovery) {
                this.setState({
                    fromNdhmDiscovery: undefined,
                    name: undefined,
                    gender: undefined,
                    authHealthId: undefined,
                });
            }
            return;
        }

        const { localId, id, personal } = patient;
        const { name, phone, age, gender, fromNdhmDiscovery } = personal;
        const { pic, phCode } = this.state;
        const referredByNumber = patient?.formData?.find((form) => form.key === 'referredbynumber');

        this.setState(
            {
                abhaId: patient.abha?.id,
                localId,
                autoSelectPt: patient,
                pic: this.isEditMode() || this.isPicFileType() ? pic : getPatientPicUrl(id),
                name: name.f + (name.l ? name.l : ''),
                phCode: phone?.c || phCode,
                phoneNumber: phone?.n,
                isPhoneNumberError: phone?.n
                    ? !checkIfValidPhoneNumber(phone?.c || '', phone?.n || '')
                    : false,
                isReferralPhoneNumberError: referredByNumber
                    ? !checkIfValidPhoneNumber('+91', `${referredByNumber?.value}`, 'IN')
                    : false,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                gender: gender,
                ptId: id,

                ...decodeAge(age),

                isNameError: false,
                isGenderError: false,
                isAgeDOBError: false,
                authHealthId: patient.personal['health-ids']
                    ? patient.personal['health-ids'][0]
                    : undefined,
                dfValues:
                    patient.formData?.reduce((agg, df) => ({ ...agg, [df.key]: df.value }), {}) ||
                    {},
                fromNdhmDiscovery,
            },
            () => {
                if (
                    this.errorValidation().success &&
                    patient.id &&
                    (!this.props.isAbdmEnabled || this.props.formState === 'add-pt')
                ) {
                    this.onSave();
                    return;
                } else {
                    this.fetchMH(patient)
                        .then((ptMH) => {
                            this.setState({
                                ptMHHistory: ptMH,
                                clearMHAfterInputChange: true,
                            });
                        })
                        .catch((error) => {
                            console.log(error);
                            captureError(error as Error);
                        });

                    if (this.props.formState === 'abha') {
                        if (patient.abha?.id) {
                            this.setAbhaFields({ skipState: 'abha_end', abhaId: patient.abha.id });
                            this.setFormStateHandle('add-pt');
                            return;
                        }
                    }

                    return;
                }
            },
        );
    };

    onAgeSelect = (
        option: {
            id: YEARS | MONTHS | DAYS;
            label: string;
            value: number;
        } | null,
    ): void => {
        if (!option) {
            this.setState({
                ageDays: undefined,
                ageMonth: undefined,
                age: undefined,
            });
            return;
        }

        if (option.id === 'DAYS') {
            this.setState({
                ageMonth: undefined,
                ageDays: option.value.toString(),
                age: undefined,
                dob: undefined,
                isDobError: false,
                isAgeDOBError: false,
            });
        } else {
            this.setState({
                ageMonth: option.value.toString(),
                ageDays: undefined,
                age: undefined,
                dob: undefined,
                isDobError: false,
                isAgeDOBError: false,
            });
        }
    };

    onSendOtp = () => {
        if (!this.props.isSendAbhaOtp) {
            return;
        }

        this.setState({ allowEkaEvent: true });

        const { autoSelectPt } = this.state;

        if (autoSelectPt) {
            dispatch({
                type: NEW_PT_FORM_DATA_AS_SELECTED,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                data: { ...autoSelectPt, localId: undefined },
            });
        } else {
            const dob = this.state?.dob;
            let eventState = { ...this.state };

            if (
                dob &&
                (dob.dd !== '0' ||
                    (dob.mm !== '0' && dob.mm !== '00') ||
                    (dob.yyyy !== '0' && dob.yyyy !== '0000'))
            ) {
                eventState = {
                    ...eventState,
                    age: undefined,
                    ageDays: undefined,
                    ageMonth: undefined,
                };
            } else {
                eventState = {
                    ...eventState,
                    dob: undefined,
                };
            }

            dispatch({
                type: NEW_PT_FORM_DATA,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                data: { ...eventState, localId: undefined },
            });
        }
    };

    onDyFormUpdate = (): void => {
        getAddPDForms().then((dForm) =>
            this.setState({
                dForm: [
                    { label: 'UHID', type: 'string', key: 'uhid' },
                    ...dForm.filter((t) => t.key !== 'uhid' && t.key !== 'pcc'),
                ],
                isPhCode: dForm.find((t) => t.key === 'pcc')?.key === 'pcc',
            }),
        );
    };

    setPic = (pic?: File) => {
        this.setState({ pic: pic });
    };

    componentDidUpdate(
        prevProps: Readonly<Props>,
        prevState: Readonly<State>,
        snapshot?: any,
    ): void {
        if (prevProps.rxDocId !== this.props.rxDocId) {
            this.getMedicalHistoryConfigData();
            this.getShowMedicalHistoryInAddPt();
        }
        if (
            (!this.props.isSendAbhaOtp || this.state.allowEkaEvent) &&
            this.props.showUseABDM &&
            !this.props.showAbhaCard &&
            this.props.formState === 'add-pt'
        ) {
            const dob = this.state?.dob;
            let eventState = { ...this.state };

            if (
                dob &&
                (dob.dd !== '0' ||
                    (dob.mm !== '0' && dob.mm !== '00') ||
                    (dob.yyyy !== '0' && dob.yyyy !== '0000'))
            ) {
                eventState = {
                    ...eventState,
                    age: undefined,
                    ageDays: undefined,
                    ageMonth: undefined,
                };
            } else {
                eventState = {
                    ...eventState,
                    dob: undefined,
                };
            }

            dispatch({
                type: NEW_PT_FORM_DATA,
                data: { ...eventState, localId: undefined },
            });
        }
    }

    toggleHasNoMedicalHistory = () => {
        track('NO_KNOWN_MH', {
            hasNoMedicalHistory: (!this.state.hasNoMedicalHistory)!.toString(),
        });
        this.setState((prev) => {
            return {
                ...prev,
                hasNoMedicalHistory: !prev.hasNoMedicalHistory,
            };
        });
    };

    resetError = () => {
        this.setState({
            isDobError: false,
            isAgeDOBError: false,
            isNameError: false,
            isGenderError: false,
            isPhoneNumberError: false,
            isReferralPhoneNumberError: false,
        });
    };

    setFormStateHandle = (val: IFormState | undefined) => {
        track('GO_TO_ADD_PT', {
            createSkipState: this.state.createSkipState ?? '',
            showAbhaCard: String(this.props.showAbhaCard),
            formState: this.props.formState,
        });
        this.props.setFormState && this.props.setFormState(val);
    };
    setShowSendOtpScreen = (val: boolean) => this.setState({ showSendOtpScreen: val });

    setPhCodeEnum = (val: CountryCode) => this.setState({ phCodeEnum: val });

    render(): ReactNode {
        const { ActionBar, actions, isLoading } = this.props;

        const {
            pic,
            gender,
            phCode,
            phoneNumber,
            name,
            dob,
            age,
            ageMonth,
            ageDays,
            isDobError,
            isPhoneInputDisabled,
            isNameError,
            isGenderError,
            isNameInputDisabled,
            isAgeInputDisabled,
            isGenderInputDisabled,
            isPhoneNumberError,
            dForm,
            dfValues,
            showMedicalHistoryInAddPt,
            medicalHistoryConfigData,
            ptMHHistory,
            hasNoMedicalHistory,
            isAgeDOBError,
            createSkipState,
            ptId,
            authHealthId,
            showSendOtpScreen,
            isReferralPhoneNumberError,
            isPhCode,
            phCodeEnum,
            fromNdhmDiscovery,
        } = this.state;
        // const isDisabled = !gender || !phoneNumber || phoneNumber.length !== 10 || !name;

        let value: {
            id: YEARS | MONTHS | DAYS;
            label: string;
            value: number;
        } | null = null;

        if (!dob || (dob.dd === '0' && dob.mm === '0' && dob.yyyy === '0')) {
            if (ageDays) {
                value = {
                    id: 'DAYS',
                    label: `${ageDays} ${getUnit('days', ageDays)}`,
                    value: parseInt(ageDays),
                };
            } else if (age) {
                value = {
                    id: 'YEARS',
                    label: `${age} ${getUnit('years', age)}`,
                    value: parseInt(age) * 12,
                };
            } else if (ageMonth) {
                const m = parseInt(ageMonth);
                if (m >= 12 && m % 12 === 0) {
                    value = {
                        id: 'YEARS',
                        label: `${m / 12} ${getUnit('years', ageMonth)}`,
                        value: m * 1,
                    };
                } else {
                    value = {
                        id: 'MONTHS',
                        label: `${ageMonth} ${getUnit('months', ageMonth)}`,
                        value: m * 1,
                    };
                }
            }
        }

        const {
            FillerComp,
            actionBarProps,
            isDialogVisible,
            setDialogVisibility,
            showAddFields,
            showUseABDM,
            configureTab,
            showMHConfigure,
            showAbhaCard,
            isSendAbhaOtp,
            patientId,
        } = this.props;

        const formState = this.props.patientEditFlow ? 'add-pt' : this.props.formState;

        return (
            <FillerComp
                Form={
                    <PatientInfoForm
                        pic={pic}
                        phCode={phCode}
                        phCodeEnum={phCodeEnum}
                        phoneNumber={phoneNumber}
                        isPhoneInputDisabled={isPhoneInputDisabled}
                        name={name}
                        isNameInputDisabled={isNameInputDisabled}
                        isAgeInputDisabled={isAgeInputDisabled}
                        isGenderInputDisabled={isGenderInputDisabled}
                        isNameError={isNameError}
                        value={value}
                        dob={dob}
                        isDobError={isDobError}
                        isAgeDOBError={isAgeDOBError}
                        gender={gender}
                        isGenderError={isGenderError}
                        dForm={dForm}
                        dfValues={dfValues}
                        onPatientAutoComplete={this.onAutoComplete}
                        onAddEkaRefer={this.onAddEkaRefer}
                        onCountryCodeChange={isPhCode ? this.onCountryCodeChange : undefined}
                        isPhCode={isPhCode}
                        onPhoneInputChange={this.onPhoneNumberChange}
                        onAgeSelect={this.onAgeSelect}
                        onDDDobChange={this.onDDDobChange}
                        onDFChange={this.onDFChange}
                        onGenderChange={this.onGenderChange}
                        onMMDobChange={this.onMMDobChange}
                        onNameInputChange={this.onNameChange}
                        onYYYYDobChange={this.onYYYYDobChange}
                        isPhoneNumberError={isPhoneNumberError}
                        setDialogVisibility={setDialogVisibility}
                        showAddFields={showAddFields}
                        showUseABDM={showUseABDM}
                        setPic={this.setPic}
                        showMedicalHistoryInAddPt={showMedicalHistoryInAddPt}
                        medicalHistoryConfigData={medicalHistoryConfigData}
                        ptMHHistory={ptMHHistory}
                        setMedicalHistory={this.setMedicalHistory}
                        toggleHasNoMedicalHistory={this.toggleHasNoMedicalHistory}
                        hasNoMedicalHistory={hasNoMedicalHistory}
                        showMHConfigure={showMHConfigure}
                        patientEditFlow={this.props.patientEditFlow}
                        isAbdmEnabled={this.props.isAbdmEnabled}
                        onAutoComplete={this.onAutoComplete}
                        ageDays={ageDays}
                        ageMonth={ageMonth}
                        setFormStateHandle={this.setFormStateHandle}
                        formState={formState}
                        clearDob={this.clearDob}
                        showAbhaCard={showAbhaCard}
                        setShowAbhaCard={this.props.setShowAbhaCardHandler}
                        abhaId={this.state.abhaId}
                        setAbhaId={(abhaId: string | undefined) => this.setState({ abhaId })}
                        setCreateSkipState={(createSkipState: string | undefined) =>
                            this.setState({ createSkipState })
                        }
                        createSkipState={createSkipState}
                        patientId={ptId || patientId}
                        authHealthId={authHealthId}
                        showSendOtpScreen={showSendOtpScreen}
                        setShowSendOtpScreen={this.setShowSendOtpScreen}
                        abhaNewFlow={this.props.abhaNewFlow}
                        setIsPhoneNumberError={(val: boolean) =>
                            this.setState({ isPhoneNumberError: val })
                        }
                        isReferralPhoneNumberError={isReferralPhoneNumberError}
                        errorValidation={this.errorValidation}
                        resetError={this.resetError}
                        setAuthHealthId={(val: string | undefined) =>
                            this.setState({ authHealthId: val })
                        }
                        onSendOtp={this.onSendOtp}
                        isSendAbhaOtp={isSendAbhaOtp}
                        fromNdhmDiscovery={fromNdhmDiscovery}
                        onRefresh={() => {
                            this.getMedicalHistoryConfigData();
                            this.getShowMedicalHistoryInAddPt();
                        }}
                        setAbhaOid={this.setAbhaOid}
                    />
                }
                ActionBar={
                    formState === 'abha' ? null : (
                        <ActionBar
                            onCancel={actions.onCancel}
                            onSave={this.onSave}
                            isDisabled={isPhoneNumberError || isLoading}
                            isAbdmEnabled={this.props.isAbdmEnabled}
                            showAbhaCard={showAbhaCard}
                            setAddPtFormState={this.setFormStateHandle}
                            setShowAbhaCardHandler={this.props.setShowAbhaCardHandler}
                            {...actionBarProps}
                        />
                    )
                }
                onDyFormUpdate={this.onDyFormUpdate}
                isDialogVisible={isDialogVisible}
                setDialogVisibility={setDialogVisibility}
                closeMe={actions.onCancel}
                configureTab={configureTab}
                formState={formState}
                onRefresh={() => {
                    this.getMedicalHistoryConfigData();
                    this.getShowMedicalHistoryInAddPt();
                }}
            />
        );
    }
}

export default connect((state: GlobalState) => ({
    rxDocId: getPrescription(state)?.doctor?._id || '',
}))(PatientInfoComp);
